import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AuthProvider } from "react-oidc-context";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { WebStorageStateStore } from "oidc-client-ts";

import { config, redirectUri } from "./config";
import { App } from "./App";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Internal tool with a handful of users: refetching on every window focus
      // is noise, and the data doesn't change behind your back very often.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 1,
    },
  },
});

const oidcConfig = {
  authority: config.authority,
  client_id: config.clientId,
  redirect_uri: redirectUri,
  post_logout_redirect_uri: redirectUri,
  response_type: "code",
  scope: "openid email profile",

  // sessionStorage, not localStorage: closing the tab ends the session, which
  // is the right default for an admin tool that may be opened on a shared or
  // client-site machine.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),

  automaticSilentRenew: true,

  // Strip ?code=&state= from the address bar after the redirect, so a refresh
  // doesn't attempt to redeem an already-used authorization code.
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AuthProvider {...oidcConfig}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </AuthProvider>
  </StrictMode>,
);
